import { currentKp } from '../utils/observations.js';
import { useState, useEffect, useRef } from 'react';
import { SEVERITY, CATEGORY } from './useEventBus.js';
import { pushToast } from '../components/AlertToast.jsx';

const API = import.meta.env.VITE_API_BASE || '/api';

// ── Space Weather ─────────────────────────────────────────────────────────────
export function useSpaceWeather(push, observer) {
  const [weather, setWeather]   = useState(null);
  const lastKpLevel = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    async function fetch_() {
      try {
        const url = observer
          ? `${API}/spaceweather?lat=${observer.lat}&lon=${observer.lon}`
          : `${API}/spaceweather`;
        const res = await fetch(url,{signal:controller.signal});
        if (!res.ok) throw new Error(`spaceweather ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setWeather(data);
          // Emit event + toast on Kp threshold crossing
          const kp = currentKp(data);
          const level = data.kp?.level   ?? 'Quiet';
          if (kp != null && lastKpLevel.current !== level) {
            if (kp >= 5) {
              const sev = kp >= 7 ? SEVERITY.CRITICAL : SEVERITY.WARNING;
              push?.({ severity: sev, category: CATEGORY.SPACEWEATHER,
                title: `☀ Geomagnetic storm: ${level}`,
                body:  `Kp ${kp.toFixed(1)} · Aurora may be visible at lower latitudes` });
              if (kp >= 5) pushToast({ severity: sev,
                title: `☀ Storm: ${level}`,
                body:  `Kp ${kp.toFixed(1)} · Check aurora layer` });
            } else if (lastKpLevel.current && lastKpLevel.current !== 'Quiet') {
              push?.({ severity: SEVERITY.NOTICE, category: CATEGORY.SPACEWEATHER,
                title: '☀ Storm subsiding',
                body:  `Kp ${kp.toFixed(1)} · Conditions improving` });
            }
            lastKpLevel.current = level;
          }
        }
      } catch { if (!cancelled) setWeather(prev => ({...prev, status:prev ? 'stale':'unavailable',refresh_failed:true})); }
    }
    fetch_();
    const id = setInterval(fetch_, 5 * 60 * 1000);
    const ageTimer = setInterval(()=>setWeather(prev=>prev ? {...prev} : prev),30000);
    return () => { cancelled = true; controller.abort(); clearInterval(id); clearInterval(ageTimer); };
  }, [observer?.lat, observer?.lon]);

  return { weather };
}

// ── Aurora ────────────────────────────────────────────────────────────────────
export function useAurora(enabled) {
  const [aurora, setAurora] = useState(null);
  useEffect(() => {
    if (!enabled) { setAurora(null); return; }
    let cancelled = false;
    async function fetch_() {
      try {
        const res = await fetch(`${API}/aurora`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAurora(data);
      } catch { /* keep last */ }
    }
    fetch_();
    const id = setInterval(fetch_, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled]);
  return { aurora };
}

// ── Aviation ──────────────────────────────────────────────────────────────────
export function useAviation(enabled, push) {
  const [aircraft, setAircraft] = useState([]);
  const reported = useRef(false);

  useEffect(() => {
    if (!enabled) { setAircraft([]); reported.current = false; return; }
    let cancelled = false;
    async function fetch_() {
      try {
        const res = await fetch(`${API}/aviation`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          // Hard cap keeps the globe scene responsive on smaller GPUs.
          // Aviation alone can return 6k+ objects; cap to 500 for renderer safety
          const all = data.aircraft || [];
          setAircraft(all.slice(0, 500));
          if (!reported.current && data.count > 0) {
            push?.({ severity: SEVERITY.INFO, category: CATEGORY.AVIATION,
              title: `✈ Aviation layer active`,
              body:  `${data.count.toLocaleString()} aircraft currently airborne` });
            reported.current = true;
          }
        }
      } catch { /* keep last */ }
    }
    fetch_();
    const id = setInterval(fetch_, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled]);

  return { aircraft };
}

// ── Conjunctions ──────────────────────────────────────────────────────────────
export function useConjunctions(enabled, push) {
  const [conjunctions, setConjunctions] = useState([]);
  const alerted = useRef(new Set());

  useEffect(() => {
    if (!enabled) { setConjunctions([]); return; }
    let cancelled = false;
    async function fetch_() {
      try {
        const res = await fetch(`${API}/conjunctions`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          const list = data.conjunctions || [];
          setConjunctions(list);

          // Emit events for newly critical conjunctions
          for (const c of list) {
            const hoursToTca = (new Date(c.tca) - Date.now()) / 3600000;
            const key = `${c.cdm_id}-${hoursToTca < 6 ? 'crit' : 'warn'}`;
            if (!alerted.current.has(key)) {
              alerted.current.add(key);
              const sev = hoursToTca < 6 ? SEVERITY.CRITICAL : SEVERITY.WARNING;
              push?.({ severity: sev, category: CATEGORY.CONJUNCTION,
                title: `⚡ Conjunction: ${c.name_1} ↔ ${c.name_2}`,
                body:  `${c.miss_distance_km?.toFixed(1)} km miss · TCA in ${hoursToTca.toFixed(1)}h`,
                norad_1: c.norad_1, norad_2: c.norad_2 });
              if (hoursToTca < 6) pushToast({ severity: sev,
                title: `⚡ CONJUNCTION in ${hoursToTca.toFixed(1)}h`,
                body:  `${c.name_1} ↔ ${c.name_2} · ${c.miss_distance_km?.toFixed(1)} km`,
                norad_1: c.norad_1 });
            }
          }
        }
      } catch { /* keep last */ }
    }
    fetch_();
    const id = setInterval(fetch_, 60 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled]);

  return { conjunctions };
}

// ── CMEs ──────────────────────────────────────────────────────────────────────
// NASA DONKI Coronal Mass Ejection catalog with WSA-ENLIL+Cone arrival
// predictions. Polls /api/cme every 5 min. Emits events + toasts when a new
// Earth-impacting CME shows up that wasn't in the previous response, so users
// see "CME inbound" alerts without manually checking the panel.
export function useCmes(push) {
  const [cmes, setCmes] = useState([]);
  const seen = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    async function fetch_() {
      try {
        const res = await fetch(`${API}/cme`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setCmes(data.cmes || []);

        // Alert on new Earth-impacting CMEs we haven't seen yet
        for (const c of data.cmes || []) {
          if (!c.earth_impact) continue;
          if (seen.current.has(c.activity_id)) continue;
          seen.current.add(c.activity_id);

          // Only alert if arrival is in the future (don't replay historicals
          // when the page first loads)
          if (!c.inbound) continue;

          const hours = c.hours_to_arrival;
          const kp = c.kp_90 || c.kp_18 || c.kp_135 || c.kp_180 || null;
          const sev = (kp != null && kp >= 7) ? SEVERITY.CRITICAL
                    : (kp != null && kp >= 5) ? SEVERITY.WARNING
                    : SEVERITY.NOTICE;
          const arrivalLabel = hours != null && hours > 0
            ? `arrival T-${Math.floor(hours)}h ${Math.floor((hours % 1) * 60)}m`
            : 'arrival imminent';
          const kpLabel = kp != null ? ` · Kp ~${kp}` : '';
          const blowLabel = c.earth_is_glancing_blow ? ' (glancing blow)' : '';

          push?.({
            severity: sev,
            category: CATEGORY.SPACEWEATHER,
            title: `☄ CME inbound${blowLabel}`,
            body: `${c.source_location || 'unknown source'} · ${c.speed_km_s || '?'} km/s · ${arrivalLabel}${kpLabel}`,
          });
          if (sev !== SEVERITY.NOTICE) {
            pushToast({
              severity: sev,
              title: `☄ CME inbound${blowLabel}`,
              body: `${arrivalLabel}${kpLabel}`,
            });
          }
        }
      } catch { /* keep last */ }
    }
    fetch_();
    const id = setInterval(fetch_, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return { cmes, inbound: cmes.filter(c => c.inbound) };
}
